/**
 * Race Summary / Race Setup insight rules.
 */

import {
  findHomeRow,
  fleetMedianForColumn,
  getRowValue,
  getTeamName,
  leaders,
  leadersMean,
  meaningfulGap,
  rankByColumn,
  toFiniteNumber,
} from '../fleetStats';
import { buildGapDetail, buildRecommendation, sortFindings } from '../templates';
import type { Evidence, Finding, InsightRow, RaceInsightInput } from '../types';
import {
  RACE_SETUP_RULES,
  RACE_SUMMARY_RULES,
  type RaceMetricRule,
} from './raceRulebook';

/** Setup trim metrics compared as absolute gap (no inherent better direction). */
const SETUP_TRIM_IDS = new Set([
  'setup_heel',
  'setup_rh',
  'setup_cant',
  'setup_rud_rake',
  'setup_wing_twist',
  'setup_jib_lead',
]);

/**
 * Concept key for a finding id (race-vmg_perc → vmg_perc → rule.concept via map).
 * Used so Results-table outcomes are not repeated from Setup / other tables.
 */
const FINDING_CONCEPT_BY_ID: Record<string, string> = {
  'race-position': 'position',
  'race-vmg_perc': 'vmg',
  'race-start_speed': 'start_speed',
  'race-tack_loss': 'tack_loss',
  'race-gybe_loss': 'gybe_loss',
  'race-setup_vmg_perc': 'vmg', // legacy id — same concept as race VMG %
  'race-setup_heel': 'heel',
  'race-setup_rh': 'ride_height',
  'race-setup_cant': 'cant',
  'race-setup_rud_rake': 'rud_rake',
  'race-setup_wing_twist': 'wing_twist',
  'race-setup_jib_lead': 'jib_lead',
};

function conceptForFinding(finding: Finding, ruleConcept?: string): string {
  if (ruleConcept) {return ruleConcept;}
  return FINDING_CONCEPT_BY_ID[finding.id] ?? finding.id;
}

/** Keep first finding per concept (callers must push summary/Results before setup). */
function pushUniqueConcept(
  findings: Finding[],
  seen: Set<string>,
  finding: Finding | null,
  ruleConcept?: string
): void {
  if (!finding) {return;}
  const concept = conceptForFinding(finding, ruleConcept);
  if (seen.has(concept)) {return;}
  seen.add(concept);
  findings.push(finding);
}

function evaluateSummaryOrModeRule(
  rule: RaceMetricRule,
  rows: InsightRow[],
  homeBoat: string,
  primaryRankKey: string
): Finding | null {
  if (rows.length < 3) {return null;}
  const homeRow = findHomeRow(rows, homeBoat);
  if (!homeRow) {return null;}

  const ascending = rule.better === 'lower';
  // Leaders defined by race position / VMG when available, else by this metric
  const leaderRows = leaders(rows, primaryRankKey, homeBoat, primaryRankKey === 'position' || primaryRankKey === 'Position');
  const fallbackLeaders =
    leaderRows.length > 0
      ? leaderRows
      : leaders(rows, rule.metric, homeBoat, ascending);
  if (fallbackLeaders.length === 0) {return null;}

  const ours = toFiniteNumber(getRowValue(homeRow, rule.metric));
  const leaderValues = fallbackLeaders
    .map((lr) => toFiniteNumber(getRowValue(lr.row, rule.metric)))
    .filter((v): v is number => v != null);
  const leadersAvg =
    leaderValues.length > 0
      ? leaderValues.reduce((a, b) => a + b, 0) / leaderValues.length
      : leadersMean(fallbackLeaders);
  const gapInfo = meaningfulGap(ours, leadersAvg, rule.threshold, rule.better);
  if (!gapInfo) {return null;}

  const leaderTeams = fallbackLeaders.map((r) => r.team);
  const evidence: Evidence[] = [
    {
      metric: rule.metric,
      label: rule.label,
      unit: rule.unit,
      ours,
      leaders: leadersAvg,
      fleetMedian: fleetMedianForColumn(rows, rule.metric),
      better: rule.better,
    },
  ];

  const severity = gapInfo.weAreWorse ? 'loss' : 'gain';
  return {
    id: `race-${rule.id}`,
    area: 'race',
    severity,
    headline: gapInfo.weAreWorse ? rule.headlineLoss : rule.headlineGain,
    detail: buildGapDetail({
      metricLabel: rule.label,
      ours,
      leaders: leadersAvg,
      unit: rule.unit,
      leaderTeams,
      weAreWorse: gapInfo.weAreWorse,
      betterIsHigher: rule.better === 'higher',
    }),
    evidence,
    recommendation: buildRecommendation({
      template: rule.recommendation,
      leaderTeams,
      metricLabel: rule.label,
      leadersValue: leadersAvg,
      unit: rule.unit,
    }),
    showMe: {
      sortKey: rule.metric,
      sortDirection: ascending ? 'asc' : 'desc',
      highlightTeams: [homeBoat, ...leaderTeams],
    },
  };
}

function evaluateSetupTrimRule(
  rule: RaceMetricRule,
  rows: InsightRow[],
  homeBoat: string
): Finding | null {
  if (rows.length < 3) {return null;}
  const homeRow = findHomeRow(rows, homeBoat);
  if (!homeRow) {return null;}

  const leaderRows = leaders(rows, 'avg_vmg_perc', homeBoat, false);
  const fallback =
    leaderRows.length > 0
      ? leaderRows
      : leaders(rows, 'position', homeBoat, true);
  if (fallback.length === 0) {return null;}

  const ours = toFiniteNumber(getRowValue(homeRow, rule.metric));
  const leaderValues = fallback
    .map((lr) => toFiniteNumber(getRowValue(lr.row, rule.metric)))
    .filter((v): v is number => v != null);
  if (ours == null || leaderValues.length === 0) {return null;}

  const leadersAvg = leaderValues.reduce((a, b) => a + b, 0) / leaderValues.length;
  const absGap = Math.abs(ours - leadersAvg);
  if (absGap < rule.threshold) {return null;}

  const leaderTeams = fallback.map((r) => r.team);
  return {
    id: `race-${rule.id}`,
    area: 'race',
    severity: 'neutral',
    headline: rule.headlineLoss,
    detail: `Your ${rule.label.toLowerCase()} was ${ours.toFixed(1)} ${rule.unit}; leaders (${leaderTeams.join(', ')}) averaged ${leadersAvg.toFixed(1)} ${rule.unit} (Δ ${absGap.toFixed(1)}).`,
    evidence: [
      {
        metric: rule.metric,
        label: rule.label,
        unit: rule.unit,
        ours,
        leaders: leadersAvg,
        fleetMedian: fleetMedianForColumn(rows, rule.metric),
        better: rule.better,
      },
    ],
    recommendation: buildRecommendation({
      template: rule.recommendation,
      leaderTeams,
      metricLabel: rule.label,
      leadersValue: leadersAvg,
      unit: rule.unit,
    }),
    showMe: {
      sortKey: rule.metric,
      sortDirection: 'desc',
      highlightTeams: [homeBoat, ...leaderTeams],
    },
  };
}

function positionFinding(summaryRows: InsightRow[], homeBoat: string): Finding | null {
  if (summaryRows.length < 3) {return null;}
  const homeRow = findHomeRow(summaryRows, homeBoat);
  if (!homeRow) {return null;}

  const posKey = toFiniteNumber(getRowValue(homeRow, 'position')) != null ? 'position' : 'Position';
  const ranked = rankByColumn(summaryRows, posKey, true);
  const homeRanked = ranked.find((r) => r.team.toLowerCase() === homeBoat.toLowerCase());
  if (!homeRanked || !Number.isFinite(homeRanked.value)) {return null;}

  const leaderRows = ranked.filter(
    (r) =>
      r.team.toLowerCase() !== homeBoat.toLowerCase() && Number.isFinite(r.value)
  ).slice(0, summaryRows.length >= 6 ? 3 : 2);
  if (leaderRows.length === 0) {return null;}

  const leaderTeams = leaderRows.map((r) => r.team);
  const ours = homeRanked.value;
  const leadersAvg = leadersMean(leaderRows);
  const weAreWorse = ours > (leadersAvg ?? ours);

  // Only emit when not already in the top group
  if (!weAreWorse && ours <= 2) {return null;}
  if (Math.abs(ours - (leadersAvg ?? ours)) < 0.5 && ours <= 3) {return null;}

  return {
    id: 'race-position',
    area: 'race',
    severity: weAreWorse ? 'loss' : 'gain',
    headline: weAreWorse ? 'Behind the leaders on race position' : 'Among the leaders on race position',
    detail: buildGapDetail({
      metricLabel: 'Race position',
      ours,
      leaders: leadersAvg,
      unit: '',
      leaderTeams,
      weAreWorse,
      betterIsHigher: false,
    }),
    evidence: [
      {
        metric: posKey,
        label: 'Position',
        unit: '',
        ours,
        leaders: leadersAvg,
        fleetMedian: fleetMedianForColumn(summaryRows, posKey),
        better: 'lower',
      },
    ],
    recommendation: weAreWorse
      ? `Focus on where you lost to ${leaderTeams.join(', ')} — start, VMG %, and maneuvers.`
      : `Hold the mode that put you with ${leaderTeams.join(', ')}.`,
    showMe: {
      sortKey: posKey,
      sortDirection: 'asc',
      highlightTeams: [homeBoat, ...leaderTeams],
    },
  };
}

export function buildRaceFindings(input: RaceInsightInput, homeBoat: string): Finding[] {
  const findings: Finding[] = [];
  const seenConcepts = new Set<string>();
  const { summaryRows, setupRows } = input;

  // Results / race-summary table first — wins concept dedupe over setup duplicates
  // (e.g. race VMG % vs straight-line VMG %, tack loss only once).
  pushUniqueConcept(findings, seenConcepts, positionFinding(summaryRows, homeBoat), 'position');

  const summaryRankKey =
    summaryRows.some((r) => toFiniteNumber(getRowValue(r, 'position')) != null)
      ? 'position'
      : 'vmg_perc_avg';

  for (const rule of RACE_SUMMARY_RULES) {
    const f = evaluateSummaryOrModeRule(rule, summaryRows, homeBoat, summaryRankKey);
    pushUniqueConcept(findings, seenConcepts, f, rule.concept);
  }

  for (const rule of RACE_SETUP_RULES) {
    const f = SETUP_TRIM_IDS.has(rule.id)
      ? evaluateSetupTrimRule(rule, setupRows, homeBoat)
      : evaluateSummaryOrModeRule(rule, setupRows, homeBoat, 'avg_vmg_perc');
    pushUniqueConcept(findings, seenConcepts, f, rule.concept);
  }

  return sortFindings(findings);
}

export function raceHomePresent(rows: InsightRow[], homeBoat: string): boolean {
  return findHomeRow(rows, homeBoat) != null ||
    rows.some((r) => getTeamName(r).toLowerCase() === homeBoat.toLowerCase());
}
